import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { linkify } from '@/lib/linkify';
import { usePresentation } from '@/context/PresentationContext';
import { FreeText, MemberName, HouseholdName, DoctorName } from '@/components/Present';
import DoctorPicker from '@/components/DoctorPicker';
import TheirDoctors from '@/components/TheirDoctors';
import MedicationSchedule, { ScheduleEditor, scheduleSummary } from '@/components/MedicationSchedule';
import { PATIENT } from '@/constants/testIds';
import { Warning, Pill, TestTube, CalendarPlus, ChatCircleDots, PaperPlaneTilt, Copy, FilePdf, DotsThreeVertical, PencilSimple, Trash, ChartLine, Rows, ClipboardText, Heartbeat, Plus, Stethoscope } from '@phosphor-icons/react';
import { ResolveDoneButton } from '@/pages/DashboardCategory';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import PatientLogForm from '@/components/PatientLogForm';
import LabTrendChart from '@/components/LabTrendChart';
import ConfirmDialog from '@/components/ConfirmDialog';

const STATUS_LABEL = { 'Open': 'Open', 'Awaiting Response': 'Waiting to hear back', 'Resolved': 'Resolved' };
const LABEL_TO_API = { 'Open': 'Open', 'Waiting to hear back': 'Awaiting Response', 'Resolved': 'Resolved' };

function calcAge(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
}

function computeMarker(l) {
    if (l.flag && l.flag !== 'Normal') {
        if (l.flag === 'Abnormal' || l.flag === 'Needs Review') return 'Needs a look';
        if (l.flag === 'Out of range' || l.flag === 'Critical') return 'Out of range';
        return l.flag;
    }
    const v = Number(l.value);
    if (isFinite(v)) {
        if (l.ref_low != null && v < Number(l.ref_low)) return 'Out of range';
        if (l.ref_high != null && v > Number(l.ref_high)) return 'Out of range';
    }
    return 'Normal';
}

function MarkerBadge({ marker }) {
    const cls = marker === 'Out of range' ? 'bg-red-100 text-red-800' :
        marker === 'Needs a look' ? 'bg-amber-100 text-amber-900' :
            'bg-emerald-50 text-emerald-700';
    return <span className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${cls}`}>{marker}</span>;
}

function LabTestPicker({ value, onPick, onCreate }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [creating, setCreating] = useState(false);
    const [nf, setNf] = useState({ test_name: '', unit: '', ref_low: '', ref_high: '' });
    const { data: tests = [] } = useQuery({
        queryKey: ['lab-tests'],
        queryFn: async () => (await api.get('/lab-tests')).data,
    });
    const filtered = tests.filter(t => t.test_name.toLowerCase().includes(q.toLowerCase()));
    const create = async () => {
        if (!nf.test_name.trim()) return;
        try {
            const { data } = await api.post('/lab-tests', {
                test_name: nf.test_name.trim(), unit: nf.unit || null,
                ref_low: nf.ref_low ? parseFloat(nf.ref_low) : null,
                ref_high: nf.ref_high ? parseFloat(nf.ref_high) : null,
            });
            toast.success('Test added');
            onCreate?.(data);
            setCreating(false); setNf({ test_name: '', unit: '', ref_low: '', ref_high: '' }); setOpen(false);
        } catch { toast.error('Failed'); }
    };
    return (
        <div>
            <Button type="button" data-testid={PATIENT.labTestPicker} variant="outline" className="w-full justify-start" onClick={() => setOpen(true)}>
                {value?.test_name || 'Choose a test…'}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Pick a test</DialogTitle></DialogHeader>
                    {!creating ? (
                        <>
                            <Input autoFocus placeholder="Search tests (e.g., A1c)" value={q} onChange={e => setQ(e.target.value)} />
                            <div className="max-h-64 overflow-auto divide-y divide-slate-100 rounded-md border border-slate-200">
                                {filtered.map(t => (
                                    <button key={t.lab_test_id} onClick={() => { onPick(t); setOpen(false); }}
                                        className="w-full text-left px-3 py-2 hover:bg-slate-50">
                                        <div className="text-sm font-medium text-slate-900">{t.test_name}</div>
                                        <div className="text-[11px] text-slate-500">
                                            {t.unit && `${t.unit} · `}
                                            {t.ref_text || (t.ref_low != null && t.ref_high != null ? `${t.ref_low}–${t.ref_high}` : t.ref_high != null ? `< ${t.ref_high}` : t.ref_low != null ? `> ${t.ref_low}` : 'no range')}
                                        </div>
                                    </button>
                                ))}
                                {filtered.length === 0 && <div className="px-3 py-4 text-sm text-slate-500">No matches.</div>}
                            </div>
                            <Button type="button" variant="outline" onClick={() => setCreating(true)} className="w-full">
                                + Add a new test type
                            </Button>
                        </>
                    ) : (
                        <div className="space-y-3">
                            <div><Label>Test name</Label><Input data-testid={PATIENT.labNewTestName} value={nf.test_name} onChange={e => setNf({ ...nf, test_name: e.target.value })} /></div>
                            <div className="grid grid-cols-3 gap-2">
                                <div><Label>Unit</Label><Input data-testid={PATIENT.labNewTestUnit} value={nf.unit} onChange={e => setNf({ ...nf, unit: e.target.value })} /></div>
                                <div><Label>Normal low</Label><Input data-testid={PATIENT.labNewTestLow} type="number" step="0.01" value={nf.ref_low} onChange={e => setNf({ ...nf, ref_low: e.target.value })} /></div>
                                <div><Label>Normal high</Label><Input data-testid={PATIENT.labNewTestHigh} type="number" step="0.01" value={nf.ref_high} onChange={e => setNf({ ...nf, ref_high: e.target.value })} /></div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                                <Button data-testid={PATIENT.labNewTestSave} onClick={create} className="bg-primary text-primary-foreground">Save</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function LabForm({ patientId, initial, onDone }) {
    const [picked, setPicked] = useState(null);
    const [f, setF] = useState({
        test_name: initial?.test_name || '',
        value: initial?.value ?? '',
        unit: initial?.unit || '',
        ref_low: initial?.ref_low ?? '',
        ref_high: initial?.ref_high ?? '',
        date: initial?.date || new Date().toISOString().slice(0, 10),
        flag: initial?.flag || 'Normal',
    });
    const pick = (t) => {
        setPicked(t);
        setF(prev => ({ ...prev, test_name: t.test_name, unit: t.unit || '', ref_low: t.ref_low ?? '', ref_high: t.ref_high ?? '' }));
    };
    const save = async () => {
        if (!f.test_name || f.value === '') { toast.error('Test and value required'); return; }
        try {
            const payload = {
                test_name: f.test_name,
                value: parseFloat(f.value), unit: f.unit || null,
                ref_low: f.ref_low === '' ? null : parseFloat(f.ref_low),
                ref_high: f.ref_high === '' ? null : parseFloat(f.ref_high),
                date: f.date, flag: f.flag,
            };
            if (initial) {
                await api.patch(`/labs/${initial.lab_id}`, payload);
            } else {
                await api.post('/labs', { patient_id: patientId, ...payload });
            }
            toast.success('Saved');
            onDone?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <div className="space-y-3">
            <div>
                <Label>Test</Label>
                <LabTestPicker
                    value={picked || (f.test_name ? { test_name: f.test_name } : null)}
                    onPick={pick}
                    onCreate={pick}
                />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Value</Label><Input data-testid={PATIENT.labValue} type="number" step="0.01" value={f.value} onChange={e => setF({ ...f, value: e.target.value })} /></div>
                <div><Label>Unit</Label><Input data-testid={PATIENT.labUnit} value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} /></div>
                <div><Label>Normal low</Label><Input data-testid={PATIENT.labRefLow} type="number" step="0.01" value={f.ref_low} onChange={e => setF({ ...f, ref_low: e.target.value })} /></div>
                <div><Label>Normal high</Label><Input data-testid={PATIENT.labRefHigh} type="number" step="0.01" value={f.ref_high} onChange={e => setF({ ...f, ref_high: e.target.value })} /></div>
                <div><Label>Date</Label><Input data-testid={PATIENT.labDate} type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} /></div>
                <div>
                    <Label>Marker</Label>
                    <Select value={f.flag} onValueChange={v => setF({ ...f, flag: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Normal">Normal</SelectItem>
                            <SelectItem value="Needs a look">Needs a look</SelectItem>
                            <SelectItem value="Out of range">Out of range</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <DialogFooter><Button data-testid={PATIENT.labSubmit} className="rounded-full bg-primary text-primary-foreground" onClick={save}>{initial ? 'Save' : 'Add result'}</Button></DialogFooter>
        </div>
    );
}

function MedForm({ patientId, initial, onDone }) {
    const [f, setF] = useState({
        name: initial?.name || '', dose: initial?.dose || '',
        prescriber: initial?.prescriber || '', provider_id: initial?.provider_id || '', refill_date: initial?.refill_date || '',
        status: initial?.status || 'Active', status_note: initial?.status_note || '',
    });
    const [schedule, setSchedule] = useState(initial?.schedule ? initial.schedule : { mode: 'none', times: [], days: [] });
    const save = async () => {
        if (!f.name.trim()) return;
        try {
            const payload = { ...f, provider_id: f.provider_id || '', prescriber: f.prescriber || null, schedule: schedule.mode === 'none' ? { mode: 'as_needed', times: [], days: [] } : schedule };
            if (initial) {
                await api.patch(`/medications/${initial.medication_id}`, payload);
            } else {
                await api.post('/medications', { patient_id: patientId, ...payload });
            }
            toast.success('Saved'); onDone?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid={PATIENT.medName} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>Dose</Label><Input data-testid={PATIENT.medDose} value={f.dose} onChange={e => setF({ ...f, dose: e.target.value })} placeholder="10 mg daily" /></div>
            <div>
                <Label>Prescribing doctor</Label>
                <DoctorPicker patientId={patientId} testid="med-doctor-picker"
                    value={f.prescriber ? { provider_id: f.provider_id, name: f.prescriber } : null}
                    onChange={(d) => setF({ ...f, provider_id: d?.provider_id || '', prescriber: d?.name || '' })} />
            </div>
            <ScheduleEditor value={schedule} onChange={setSchedule} />
            <div><Label>Refill date</Label><Input data-testid={PATIENT.medRefill} type="date" value={f.refill_date || ''} onChange={e => setF({ ...f, refill_date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Status</Label>
                    <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Stopped">Stopped</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {f.status !== 'Active' && (
                    <div><Label>Reason stopped</Label><Input value={f.status_note} onChange={e => setF({ ...f, status_note: e.target.value })} placeholder="side effects…" /></div>
                )}
            </div>
            <DialogFooter><Button data-testid={PATIENT.medSubmit} className="rounded-full bg-primary text-primary-foreground" onClick={save}>{initial ? 'Save' : 'Add'}</Button></DialogFooter>
        </div>
    );
}

function ApptForm({ patientId, initial, onDone }) {
    const [f, setF] = useState({
        title: initial?.title || '', provider_name: initial?.provider_name || '', provider_id: initial?.provider_id || '',
        appt_type: initial?.appt_type || 'Visit',
        status: initial?.status || 'Scheduled',
        scheduled_at: initial?.scheduled_at ? new Date(initial.scheduled_at).toISOString().slice(0, 16) : '',
        public_note: initial?.public_note || '',
    });
    const save = async () => {
        if (!f.title) return;
        try {
            const payload = { ...f, scheduled_at: f.scheduled_at || null, provider_id: f.provider_id || '', provider_name: f.provider_name || null };
            if (initial) {
                await api.patch(`/appointments/${initial.appointment_id}`, payload);
            } else {
                await api.post('/appointments', { patient_id: patientId, ...payload });
            }
            toast.success('Saved'); onDone?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid={PATIENT.apptTitle} value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="Annual check-up" /></div>
            <div>
                <Label>Doctor</Label>
                <DoctorPicker patientId={patientId} testid={PATIENT.apptProvider}
                    value={f.provider_name ? { provider_id: f.provider_id, name: f.provider_name } : null}
                    onChange={(d) => setF({ ...f, provider_id: d?.provider_id || '', provider_name: d?.name || '' })} />
            </div>
            <div><Label>Date/time</Label><Input data-testid={PATIENT.apptDate} type="datetime-local" value={f.scheduled_at} onChange={e => setF({ ...f, scheduled_at: e.target.value })} /></div>
            <div>
                <Label>Status</Label>
                <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
                    <SelectTrigger data-testid={PATIENT.apptStatus}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {['Needs Scheduling', 'Scheduled', 'Completed', 'Cancelled'].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label>Note for the fridge board <span className="text-slate-400 font-normal">(optional — public-safe, no medical details)</span></Label>
                <Input
                    data-testid="appt-public-note"
                    value={f.public_note}
                    onChange={e => setF({ ...f, public_note: e.target.value })}
                    placeholder='e.g. "Maria will drive you" or "Bring your reading glasses"'
                />
            </div>
            <DialogFooter><Button data-testid={PATIENT.apptSubmit} className="rounded-full bg-primary text-primary-foreground" onClick={save}>{initial ? 'Save' : 'Save'}</Button></DialogFooter>
        </div>
    );
}

function ExportLogButton({ patientId }) {
    const { on: presentationOn } = usePresentation();
    const [open, setOpen] = useState(false);
    const [range, setRange] = useState('this_month');
    const [busy, setBusy] = useState(false);
    const compute = () => {
        const today = new Date();
        const iso = (d) => d.toISOString().slice(0, 10);
        if (range === 'all') return {};
        if (range === 'last_30') { const s = new Date(today); s.setDate(s.getDate() - 30); return { date_from: iso(s), date_to: iso(today) }; }
        if (range === 'last_month') { const first = new Date(today.getFullYear(), today.getMonth() - 1, 1); const last = new Date(today.getFullYear(), today.getMonth(), 0); return { date_from: iso(first), date_to: iso(last) }; }
        const first = new Date(today.getFullYear(), today.getMonth(), 1); return { date_from: iso(first), date_to: iso(today) };
    };
    const download = async () => {
        setBusy(true);
        try {
            const res = await api.get(`/patients/${patientId}/log/export`, { params: compute(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a'); a.href = url;
            const m = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/);
            a.download = m ? m[1] : `care-notes-${patientId}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('PDF downloaded'); setOpen(false);
        } catch { toast.error('Export failed'); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid={PATIENT.exportPdf} size="sm" variant="outline"
                    disabled={presentationOn}
                    title={presentationOn ? 'Export is disabled while presentation mode is on' : ''}
                    className="rounded-full min-h-[44px] sm:min-h-0">
                    <FilePdf size={14} className="mr-1" /> Export PDF
                    {presentationOn && <span className="ml-1 text-[9px] uppercase tracking-widest text-slate-400">(disabled)</span>}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl">Export care notes</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-600 -mt-1">A printable summary of the care notes for a chosen period.</p>
                <div>
                    <Label>Date range</Label>
                    <Select value={range} onValueChange={setRange}>
                        <SelectTrigger data-testid={PATIENT.exportRange}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="this_month">This month</SelectItem>
                            <SelectItem value="last_month">Last month</SelectItem>
                            <SelectItem value="last_30">Last 30 days</SelectItem>
                            <SelectItem value="all">All time</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <DialogFooter><Button onClick={download} disabled={busy} className="rounded-full bg-primary text-primary-foreground">{busy ? 'Preparing…' : 'Download PDF'}</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SendCheckInDialog({ patientId, patientName, onDone }) {
    const [open, setOpen] = useState(false);
    const [surveyId, setSurveyId] = useState('');
    const [assignment, setAssignment] = useState(null);
    const { data: surveys = [] } = useQuery({ queryKey: ['surveys'], queryFn: async () => (await api.get('/surveys')).data, enabled: open });
    const submit = async () => {
        if (!surveyId) return;
        try {
            const { data } = await api.post('/assignments', { survey_id: surveyId, patient_id: patientId });
            setAssignment(data);
            toast.success('Check-in link ready'); onDone?.();
        } catch { toast.error('Failed'); }
    };
    const publicUrl = assignment ? `${window.location.origin}/check-in/${assignment.token}` : '';
    const textBody = `Hi ${patientName ? patientName.split(' ')[0] : ''} — here’s your Tanow check-in: ${publicUrl}`;
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setAssignment(null); setSurveyId(''); } }}>
            <DialogTrigger asChild>
                <Button data-testid={PATIENT.sendSurvey} size="sm" className="rounded-full bg-primary text-primary-foreground">
                    <PaperPlaneTilt size={14} className="mr-1" /> Send check-in
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl">Send check-in</DialogTitle></DialogHeader>
                {!assignment ? (
                    <div className="space-y-3">
                        <div>
                            <Label>Choose a check-in</Label>
                            <Select value={surveyId} onValueChange={setSurveyId}>
                                <SelectTrigger><SelectValue placeholder="Pick a check-in" /></SelectTrigger>
                                <SelectContent>{surveys.map(s => <SelectItem key={s.survey_id} value={s.survey_id}>{s.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        {surveys.length === 0 && <div className="text-sm text-slate-600">No check-ins yet. <Link to="/check-ins" className="text-primary underline">Create one</Link>.</div>}
                        <DialogFooter><Button data-testid={PATIENT.sendSurveySubmit} onClick={submit} disabled={!surveyId} className="rounded-full bg-primary text-primary-foreground">Create link</Button></DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="text-sm text-slate-600">
                            Here’s the unique link — no login required. Paste it into your texting app or email it below.
                        </div>
                        <div className="flex items-center gap-2">
                            <Input data-testid={PATIENT.assignmentLink} readOnly value={publicUrl} className="font-mono text-xs" />
                            <Button
                                variant="outline" size="icon"
                                onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }}
                                className="shrink-0"
                            ><Copy size={16} /></Button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                            <Button
                                data-testid="send-checkin-copy-sms"
                                className="rounded-full h-11 bg-primary text-primary-foreground"
                                onClick={() => {
                                    navigator.clipboard.writeText(textBody);
                                    toast.success('Copied — paste it into your text app');
                                }}
                            >
                                <PaperPlaneTilt size={16} className="mr-1.5" /> Copy link to send by text
                            </Button>
                            <Button
                                data-testid="send-checkin-email"
                                variant="outline" className="rounded-full h-11"
                                onClick={() => {
                                    const subject = encodeURIComponent('A quick check-in from Tanow');
                                    const body = encodeURIComponent(`Hi,\n\nWhen you have a moment, please answer a quick check-in. It takes about 1–2 minutes and no login is needed:\n\n${publicUrl}\n\nThank you — and if this is an emergency, please call 911.`);
                                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                                }}
                            >
                                Send by email
                            </Button>
                        </div>
                        <div className="text-[11px] text-slate-500">
                            Tanow doesn’t send the text for you — you send it from your own phone so it comes from a number your loved one knows.
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function VitalsPanel({ patientId, readings, onChange }) {
    const [open, setOpen] = useState(false);
    const [type, setType] = useState('Blood pressure');
    const [value, setValue] = useState('');
    const [sys, setSys] = useState('');
    const [dia, setDia] = useState('');
    const [takenAt, setTakenAt] = useState(new Date().toISOString().slice(0, 16));
    const [err, setErr] = useState(null);
    const reset = () => { setValue(''); setSys(''); setDia(''); setErr(null); setTakenAt(new Date().toISOString().slice(0, 16)); };
    const submit = async () => {
        setErr(null);
        try {
            const payload = {
                reading_type: type,
                taken_at: takenAt ? new Date(takenAt).toISOString() : null,
            };
            if (type === 'Blood pressure') {
                const s = Number(sys), d = Number(dia);
                if (!Number.isFinite(s) || !Number.isFinite(d) || s < 60 || s > 250 || d < 30 || d > 150 || s <= d) {
                    setErr("That doesn't look right — blood pressure is usually written like 120/80.");
                    return;
                }
                payload.systolic = s; payload.diastolic = d; payload.unit = 'mmHg';
            } else {
                const v = Number(value);
                if (!Number.isFinite(v)) { setErr('Enter a number, e.g. 154'); return; }
                payload.value = v; payload.unit = 'lbs';
            }
            await api.post(`/patients/${patientId}/readings`, payload);
            toast.success('Reading added');
            reset(); setOpen(false); onChange?.();
        } catch (e) {
            setErr(e.response?.data?.detail || 'Save failed');
        }
    };
    const remove = async (r) => {
        try {
            await api.delete(`/readings/${r.reading_id}`);
            toast.success('Deleted'); onChange?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm text-slate-600">{readings.length} reading{readings.length === 1 ? '' : 's'}</div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                    <DialogTrigger asChild>
                        <Button data-testid={PATIENT.addReading} size="sm" className="rounded-full bg-primary text-primary-foreground">
                            <Plus size={14} className="mr-1" /> Add reading
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading text-2xl">Add reading</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Type</Label>
                                <Select value={type} onValueChange={(v) => { setType(v); setErr(null); }}>
                                    <SelectTrigger data-testid={PATIENT.readingType}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Blood pressure">Blood pressure</SelectItem>
                                        <SelectItem value="Weight">Weight</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {type === 'Blood pressure' ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Top number</Label>
                                        <Input data-testid={PATIENT.readingSystolic} type="number" value={sys} onChange={e => setSys(e.target.value)} placeholder="120" />
                                    </div>
                                    <div>
                                        <Label>Bottom number</Label>
                                        <Input data-testid={PATIENT.readingDiastolic} type="number" value={dia} onChange={e => setDia(e.target.value)} placeholder="80" />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <Label>Weight (lbs)</Label>
                                    <Input data-testid={PATIENT.readingValue} type="number" step="0.1" value={value} onChange={e => setValue(e.target.value)} />
                                </div>
                            )}
                            <div>
                                <Label>When</Label>
                                <Input type="datetime-local" value={takenAt} onChange={e => setTakenAt(e.target.value)} />
                            </div>
                            {err && <div className="text-sm rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2">{err}</div>}
                        </div>
                        <DialogFooter>
                            <Button data-testid={PATIENT.readingSubmit} onClick={submit} className="rounded-full bg-primary text-primary-foreground">Save reading</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            {readings.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-14 text-center">
                    <Heartbeat size={32} className="mx-auto text-slate-400" />
                    <div className="font-heading text-xl mt-3 text-slate-800">No readings yet</div>
                    <div className="text-sm text-slate-500 mt-1">Add one manually, or send a check-in with a blood-pressure or weight question.</div>
                </div>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-card shadow-sm divide-y divide-slate-200">
                    {readings.map(r => (
                        <div key={r.reading_id} data-testid={PATIENT.readingRow} className="flex items-center gap-3 px-5 py-3">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center ${r.reading_type === 'Blood pressure' ? 'bg-rose-50 text-rose-700' : 'bg-sky-50 text-sky-700'}`}>
                                <Heartbeat size={18} weight="duotone" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-900">
                                    {r.reading_type === 'Blood pressure'
                                        ? <>{Math.round(r.systolic)}/{Math.round(r.diastolic)} <span className="text-xs text-slate-500 font-normal">mmHg</span></>
                                        : <>{r.value} <span className="text-xs text-slate-500 font-normal">{r.unit || 'lbs'}</span></>}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {new Date(r.taken_at).toLocaleString()} · <span className={r.source === 'Check-in' ? 'text-primary' : ''}>{r.source}</span>
                                </div>
                            </div>
                            <button data-testid={PATIENT.readingDelete} onClick={() => remove(r)} className="text-slate-400 hover:text-red-600 p-1.5 rounded">
                                <Trash size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function MemberCheckInsList({ assignments, logs, onChange }) {
    const [cancelTarget, setCancelTarget] = useState(null);
    const logByAssignment = useMemo(() => {
        const m = {};
        for (const l of logs) {
            if (l.survey_assignment_id) m[l.survey_assignment_id] = l;
        }
        return m;
    }, [logs]);
    const sorted = useMemo(
        () => [...assignments].sort((a, b) => (b.assigned_at || '').localeCompare(a.assigned_at || '')),
        [assignments]
    );
    const doCancel = async () => {
        try {
            await api.post(`/assignments/${cancelTarget.assignment_id}/cancel`);
            toast.success('Check-in cancelled');
            setCancelTarget(null);
            onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not cancel');
        }
    };
    if (sorted.length === 0) {
        return (
            <div className="rounded-xl border border-slate-200 bg-card p-6 text-sm text-slate-500" data-testid="member-check-ins-empty">
                No check-ins sent yet.
            </div>
        );
    }
    return (
        <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden" data-testid="member-check-ins-list">
            <div className="grid grid-cols-[1.4fr_1fr_160px_140px] px-4 py-2.5 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <div>Check-in</div><div>Sent</div><div>Status</div><div className="text-right">Actions</div>
            </div>
            <div className="divide-y divide-slate-100">
                {sorted.map(a => {
                    const cancelled = a.status === 'Cancelled';
                    const answered = a.status === 'Completed' || a.status === 'Reviewed';
                    const linkedLog = logByAssignment[a.assignment_id];
                    const statusLabel = cancelled ? 'Cancelled'
                        : answered ? `Answered ${a.completed_at ? new Date(a.completed_at).toLocaleDateString() : ''}`
                        : 'Waiting to be answered';
                    const cls = cancelled ? 'bg-slate-100 text-slate-600'
                        : answered ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-amber-50 text-amber-900';
                    return (
                        <div key={a.assignment_id} data-testid="member-check-ins-row" className="grid grid-cols-[1.4fr_1fr_160px_140px] px-4 py-3 items-center text-sm">
                            <div className="font-medium text-slate-900 truncate">{a.survey_name}</div>
                            <div className="text-slate-600">{new Date(a.assigned_at).toLocaleString()}</div>
                            <div>
                                <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${cls}`}>{statusLabel}</span>
                            </div>
                            <div className="text-right space-x-2">
                                {answered && linkedLog && (
                                    <a href={`#log-${linkedLog.log_id}`} className="text-primary hover:underline text-sm" data-testid="member-check-in-view-answer">
                                        View answer
                                    </a>
                                )}
                                {!answered && !cancelled && (
                                    <Button size="sm" variant="outline" onClick={() => setCancelTarget(a)}
                                        className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                                        data-testid="member-check-in-cancel">
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <ConfirmDialog
                open={!!cancelTarget}
                onOpenChange={(v) => !v && setCancelTarget(null)}
                title="Cancel this check-in?"
                description={cancelTarget ? `${cancelTarget.patient_name || 'They'} won't be able to answer it.` : ''}
                confirmLabel="Yes, cancel it"
                onConfirm={doCancel}
            />
        </div>
    );
}

export default function MemberProfile() {
    const { patientId } = useParams();
    const qc = useQueryClient();
    const { maskName } = usePresentation();
    const [labView, setLabView] = useState('chart');
    const [labFilter, setLabFilter] = useState('all');
    const [editMember, setEditMember] = useState(false);
    const [delMember, setDelMember] = useState(false);
    const [labDialog, setLabDialog] = useState({ open: false, initial: null });
    const [medDialog, setMedDialog] = useState({ open: false, initial: null });
    const [apptDialog, setApptDialog] = useState({ open: false, initial: null });
    const [logDialog, setLogDialog] = useState({ open: false, initial: null });
    const [confirmDel, setConfirmDel] = useState(null); // { kind, id, label }

    const { data: patient } = useQuery({ queryKey: ['patient', patientId], queryFn: async () => (await api.get(`/patients/${patientId}`)).data });
    const [logsLimit, setLogsLimit] = useState(10);
    const { data: logsData = { items: [], total: 0 } } = useQuery({
        queryKey: ['logs', patientId, logsLimit],
        queryFn: async () => (await api.get('/patient-logs', {
            params: { patient_id: patientId, limit: logsLimit, include_total: true }
        })).data,
    });
    const logs = logsData.items || [];
    const logsTotal = logsData.total || 0;
    const { data: meds = [] } = useQuery({ queryKey: ['meds', patientId], queryFn: async () => (await api.get('/medications', { params: { patient_id: patientId } })).data });
    const { data: labs = [] } = useQuery({ queryKey: ['labs', patientId], queryFn: async () => (await api.get('/labs', { params: { patient_id: patientId } })).data });
    const { data: appts = [] } = useQuery({ queryKey: ['appts', patientId], queryFn: async () => (await api.get('/appointments', { params: { patient_id: patientId } })).data });
    const { data: readings = [] } = useQuery({ queryKey: ['readings', patientId], queryFn: async () => (await api.get(`/patients/${patientId}/readings`)).data });
    const { data: assignments = [] } = useQuery({ queryKey: ['assignments', patientId], queryFn: async () => (await api.get('/assignments', { params: { patient_id: patientId } })).data });

    const labsByTest = useMemo(() => {
        const m = {};
        for (const l of labs) { (m[l.test_name] = m[l.test_name] || []).push(l); }
        return m;
    }, [labs]);
    const testNames = Object.keys(labsByTest).sort();
    const labsForTable = useMemo(() => {
        const filtered = labFilter === 'all' ? labs : labs.filter(l => l.test_name === labFilter);
        return [...filtered].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    }, [labs, labFilter]);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['patient', patientId] });
        qc.invalidateQueries({ queryKey: ['logs', patientId] });
        qc.invalidateQueries({ queryKey: ['meds', patientId] });
        qc.invalidateQueries({ queryKey: ['med-log', patientId] });
        qc.invalidateQueries({ queryKey: ['labs', patientId] });
        qc.invalidateQueries({ queryKey: ['appts', patientId] });
        qc.invalidateQueries({ queryKey: ['readings', patientId] });
        qc.invalidateQueries({ queryKey: ['assignments', patientId] });
    };

    const doConfirmDelete = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(confirmDel.url);
            toast.success('Deleted'); setConfirmDel(null); refresh();
        } catch { toast.error('Failed'); }
    };
    const doDeleteMember = async () => {
        try {
            await api.delete(`/patients/${patientId}`);
            toast.success('Removed');
            window.location.href = patient?.facility_id ? `/households/${patient.facility_id}` : '/households';
        } catch { toast.error('Failed'); }
    };

    if (!patient) return <div className="p-10 text-slate-500 text-sm">Loading…</div>;
    const age = calcAge(patient.dob);

    return (
        <div data-testid={PATIENT.root} className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-7xl">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                <Link to={`/households/${patient.facility_id}`} className="hover:underline">Household</Link> · Profile
            </div>
            <header className="mb-6">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-slate-900 break-words">
                            <MemberName name={patient.name} />
                            {age != null && <span className="text-slate-500 font-normal ml-3">· {age}y</span>}
                        </h1>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {(patient.conditions || []).map(c => <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{c}</span>)}
                        </div>
                    </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="rounded-full"><DotsThreeVertical size={18} weight="bold" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid={PATIENT.memberEdit} onClick={() => setEditMember(true)}><PencilSimple size={14} className="mr-2" /> Edit profile</DropdownMenuItem>
                            <DropdownMenuItem data-testid={PATIENT.memberDelete} onClick={() => setDelMember(true)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Remove from household</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                {patient.allergies?.length > 0 && (
                    <div data-testid={PATIENT.allergyBanner} className="mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                        <Warning size={20} weight="fill" />
                        <div className="text-sm"><span className="font-semibold uppercase tracking-widest text-[11px] mr-2">Allergies</span>{patient.allergies.join(', ')}</div>
                    </div>
                )}
            </header>

            <div>
                <div>
                    <Tabs defaultValue="log">
                        <TabsList className="flex-wrap h-auto justify-start gap-1 [&>button]:min-h-[44px] md:[&>button]:min-h-0">
                            <TabsTrigger data-testid={PATIENT.tabLog} value="log"><ChatCircleDots size={14} className="mr-1" /> Care notes</TabsTrigger>
                            <TabsTrigger data-testid={PATIENT.tabMeds} value="meds"><Pill size={14} className="mr-1" /> Medications</TabsTrigger>
                            <TabsTrigger data-testid={PATIENT.tabLabs} value="labs"><TestTube size={14} className="mr-1" /> Labs</TabsTrigger>
                            <TabsTrigger data-testid={PATIENT.tabVitals} value="vitals"><Heartbeat size={14} className="mr-1" /> Vitals</TabsTrigger>
                            <TabsTrigger data-testid={PATIENT.tabAppointments} value="appts"><CalendarPlus size={14} className="mr-1" /> Appointments</TabsTrigger>
                            <TabsTrigger data-testid={PATIENT.tabSurveys} value="surveys"><PaperPlaneTilt size={14} className="mr-1" /> Check-ins</TabsTrigger>
                            <TabsTrigger data-testid="member-tab-doctors" value="doctors"><Stethoscope size={14} className="mr-1" /> Their doctors</TabsTrigger>
                        </TabsList>

                        <TabsContent value="log" className="mt-4">
                            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                                <div className="text-sm text-slate-600" data-testid="member-notes-total">
                                    {logsTotal} note{logsTotal === 1 ? '' : 's'}
                                </div>
                                <div className="flex items-center gap-2">
                                    <ExportLogButton patientId={patientId} />
                                    <Button data-testid="member-note-add" size="sm" className="rounded-full bg-primary text-primary-foreground min-h-[44px] md:min-h-0" onClick={() => setLogDialog({ open: true, initial: null })}>Add care note</Button>
                                </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-card shadow-sm">
                                {logs.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No notes yet — use “Add care note” above to add the first one.</div>}
                                <div className="divide-y divide-slate-200">
                                    {logs.map(l => (
                                        <div key={l.log_id} data-testid={PATIENT.logEntry} className="px-4 sm:px-5 py-4">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{l.entry_type}</span>
                                                <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${l.status === 'Resolved' ? 'bg-emerald-50 text-emerald-700' : l.status === 'Awaiting Response' ? 'bg-amber-50 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{STATUS_LABEL[l.status] || l.status}</span>
                                                {l.attention_flag && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-[#F59E0B] text-black">! Attention</span>}
                                                {l.reviewed_at && <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">Reviewed</span>}
                                                <span className="text-xs text-slate-500 ml-auto">{new Date(l.entry_at).toLocaleString()}</span>
                                                {l.status !== 'Resolved' && (
                                                    <ResolveDoneButton log={l} onDone={refresh} testid={`member-resolve-${l.log_id}`} />
                                                )}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button className="p-2 rounded hover:bg-slate-100 text-slate-500 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1 flex items-center justify-center" aria-label="More"><DotsThreeVertical size={18} weight="bold" /></button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem data-testid={PATIENT.logEdit} onClick={() => setLogDialog({ open: true, initial: l })}>
                                                            <PencilSimple size={14} className="mr-2" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem data-testid={PATIENT.logDelete}
                                                            onClick={() => setConfirmDel({ url: `/patient-logs/${l.log_id}`, label: 'this care note' })}
                                                            className="text-red-600 focus:text-red-700">
                                                            <Trash size={14} className="mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                            <div className="mt-2 text-sm whitespace-pre-wrap text-slate-800"><FreeText>{linkify(l.note)}</FreeText></div>
                                            <div className="mt-2 text-xs text-slate-500">
                                                by {l.created_by_name || l.created_by}
                                                {l.reviewed_by_name && ` · reviewed by ${l.reviewed_by_name}`}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {logs.length < logsTotal && (
                                <div className="mt-4 flex justify-center">
                                    <Button
                                        data-testid="member-notes-load-more"
                                        variant="outline"
                                        className="rounded-full min-h-[44px]"
                                        onClick={() => setLogsLimit(logsLimit + 10)}
                                    >
                                        Load more ({logsTotal - logs.length} remaining)
                                    </Button>
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="meds" className="mt-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-sm text-slate-600">{meds.filter(m => m.status === 'Active').length} active · {meds.filter(m => m.status !== 'Active').length} stopped</div>
                                <Button data-testid={PATIENT.medAdd} size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={() => setMedDialog({ open: true, initial: null })}>Add medication</Button>
                            </div>
                            <MedicationSchedule patientId={patientId} meds={meds} />
                            <div className="rounded-xl border border-slate-200 bg-card shadow-sm divide-y divide-slate-200">
                                {meds.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No medications recorded.</div>}
                                {meds.map(m => {
                                    const dc = m.status !== 'Active';
                                    return (
                                        <div key={m.medication_id} className="px-5 py-4 flex items-start gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className={`text-base font-medium ${dc ? 'line-through text-slate-400' : 'text-slate-900'}`}>{m.name} <span className="text-slate-500 font-normal">· {m.dose}</span></div>
                                                <div className={`text-xs mt-1 ${dc ? 'text-slate-400' : 'text-slate-500'}`}>
                                                    {m.prescriber && <>Rx <DoctorName name={m.prescriber} /> · </>}
                                                    {m.refill_date && `refill ${m.refill_date} · `}
                                                    {scheduleSummary(m.schedule)}
                                                </div>
                                                {dc && m.status_note && <div className="text-xs mt-1 text-slate-500 italic">Stopped: {m.status_note}</div>}
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="p-2 rounded hover:bg-slate-100 text-slate-500 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1.5 flex items-center justify-center" aria-label="More"><DotsThreeVertical size={18} weight="bold" /></button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem data-testid={PATIENT.medEdit} onClick={() => setMedDialog({ open: true, initial: m })}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                                    <DropdownMenuItem data-testid={PATIENT.medDelete} onClick={() => setConfirmDel({ url: `/medications/${m.medication_id}`, label: `${m.name}` })} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    );
                                })}
                            </div>
                        </TabsContent>

                        <TabsContent value="labs" className="mt-4">
                            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                                <div className="flex items-center gap-3">
                                    <div className="text-sm text-slate-600">{testNames.length} test{testNames.length === 1 ? '' : 's'} tracked</div>
                                    <div className="inline-flex rounded-full border border-slate-200 bg-white overflow-hidden">
                                        <button data-testid={PATIENT.labViewChart} onClick={() => setLabView('chart')}
                                            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 ${labView === 'chart' ? 'bg-primary text-primary-foreground' : 'text-slate-600'}`}>
                                            <ChartLine size={13} /> Chart
                                        </button>
                                        <button data-testid={PATIENT.labViewTable} onClick={() => setLabView('table')}
                                            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 ${labView === 'table' ? 'bg-primary text-primary-foreground' : 'text-slate-600'}`}>
                                            <Rows size={13} /> Table
                                        </button>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {labView === 'table' && (
                                        <Select value={labFilter} onValueChange={setLabFilter}>
                                            <SelectTrigger data-testid={PATIENT.labFilter} className="w-40"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All tests</SelectItem>
                                                {testNames.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    )}
                                    <Button data-testid={PATIENT.labAdd} size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={() => setLabDialog({ open: true, initial: null })}>Add result</Button>
                                </div>
                            </div>

                            {testNames.length === 0 ? (
                                <div className="rounded-xl border border-slate-200 bg-card p-10 text-center text-sm text-slate-500">No lab results yet.</div>
                            ) : labView === 'chart' ? (
                                <div className="space-y-4">
                                    {testNames.map(test => (
                                        <div key={test} className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="font-heading text-xl text-slate-900">{test}</div>
                                                <div className="text-xs text-slate-500">
                                                    {labsByTest[test][0]?.ref_low != null && labsByTest[test][0]?.ref_high != null && `Normal ${labsByTest[test][0].ref_low}–${labsByTest[test][0].ref_high} ${labsByTest[test][0].unit || ''}`}
                                                </div>
                                            </div>
                                            <LabTrendChart labs={labsByTest[test]} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                                    <div className="hidden md:grid grid-cols-[110px_1fr_90px_70px_1fr_130px_40px] px-4 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                                        <div>Date</div><div>Test</div><div>Value</div><div>Unit</div><div>Normal range</div><div>Marker</div><div />
                                    </div>
                                    {labsForTable.map(l => (
                                        <div key={l.lab_id} className="border-b border-slate-100 last:border-b-0">
                                            {/* Desktop row */}
                                            <div className="hidden md:grid grid-cols-[110px_1fr_90px_70px_1fr_130px_40px] px-4 py-3 items-center text-sm">
                                                <div className="text-slate-500">{(l.date || '').slice(0, 10)}</div>
                                                <div className="text-slate-900">{l.test_name}</div>
                                                <div className="text-slate-900 font-medium">{l.value}</div>
                                                <div className="text-slate-500">{l.unit || '—'}</div>
                                                <div className="text-slate-500">
                                                    {l.ref_low != null && l.ref_high != null ? `${l.ref_low}–${l.ref_high}` :
                                                        l.ref_high != null ? `< ${l.ref_high}` :
                                                            l.ref_low != null ? `> ${l.ref_low}` : '—'}
                                                </div>
                                                <div><MarkerBadge marker={computeMarker(l)} /></div>
                                                <div>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button className="p-2 rounded hover:bg-slate-100 text-slate-500 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1 flex items-center justify-center" aria-label="More"><DotsThreeVertical size={16} weight="bold" /></button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem data-testid={PATIENT.labEdit} onClick={() => setLabDialog({ open: true, initial: l })}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                                            <DropdownMenuItem data-testid={PATIENT.labDelete} onClick={() => setConfirmDel({ url: `/labs/${l.lab_id}`, label: 'this lab result' })} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                            {/* Mobile card */}
                                            <div className="md:hidden px-4 py-3">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <div className="text-sm text-slate-900 font-medium">{l.test_name}</div>
                                                        <div className="text-xs text-slate-500">{(l.date || '').slice(0, 10)}</div>
                                                    </div>
                                                    <MarkerBadge marker={computeMarker(l)} />
                                                </div>
                                                <div className="mt-2 flex items-end justify-between gap-3">
                                                    <div>
                                                        <div className="text-2xl font-heading text-slate-900 leading-none">
                                                            {l.value}<span className="text-slate-500 text-sm font-normal ml-1">{l.unit || ''}</span>
                                                        </div>
                                                        <div className="text-[11px] text-slate-500 mt-1">
                                                            {l.ref_low != null && l.ref_high != null ? `Normal ${l.ref_low}–${l.ref_high}` :
                                                                l.ref_high != null ? `Normal < ${l.ref_high}` :
                                                                    l.ref_low != null ? `Normal > ${l.ref_low}` : ''}
                                                        </div>
                                                    </div>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 min-h-[44px] min-w-[44px] flex items-center justify-center" aria-label="More"><DotsThreeVertical size={18} weight="bold" /></button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => setLabDialog({ open: true, initial: l })}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => setConfirmDel({ url: `/labs/${l.lab_id}`, label: 'this lab result' })} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>

                        <TabsContent value="appts" className="mt-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-sm text-slate-600">{appts.length} appointment{appts.length === 1 ? '' : 's'}</div>
                                <Button data-testid={PATIENT.apptAdd} size="sm" className="rounded-full bg-primary text-primary-foreground" onClick={() => setApptDialog({ open: true, initial: null })}>Add appointment</Button>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-card shadow-sm divide-y divide-slate-200">
                                {appts.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">No appointments.</div>}
                                {appts.map(a => (
                                    <div key={a.appointment_id} className="px-5 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-slate-900">{a.title}</span>
                                            <span className={`ml-auto text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${
                                                a.status === 'Scheduled' ? 'bg-emerald-50 text-emerald-700' :
                                                a.status === 'Completed' ? 'bg-slate-100 text-slate-700' :
                                                a.status === 'Cancelled' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                                            }`}>{a.status}</span>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <button className="p-1 rounded hover:bg-slate-100 text-slate-500" aria-label="More"><DotsThreeVertical size={16} weight="bold" /></button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem data-testid={PATIENT.apptEdit} onClick={() => setApptDialog({ open: true, initial: a })}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                                    <DropdownMenuItem data-testid={PATIENT.apptDelete} onClick={() => setConfirmDel({ url: `/appointments/${a.appointment_id}`, label: 'this appointment' })} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {a.provider_name && <><DoctorName name={a.provider_name} /> · </>}
                                            {a.scheduled_at ? new Date(a.scheduled_at).toLocaleString() : 'Not yet scheduled'}
                                        </div>
                                        {(a.status === 'Scheduled' || a.status === 'Needs Scheduling') && (
                                            <Link
                                                to={`/prep/${a.appointment_id}`}
                                                data-testid={PATIENT.apptPrep}
                                                className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                <ClipboardText size={12} /> Prep for this visit
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </TabsContent>

                        <TabsContent value="surveys" className="mt-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-sm text-slate-600">Send a check-in your loved one can answer in any browser.</div>
                                <SendCheckInDialog patientId={patientId} patientName={maskName(patient?.name)} onDone={refresh} />
                            </div>
                            <MemberCheckInsList assignments={assignments} logs={logs} onChange={refresh} />
                            <div className="mt-4 rounded-xl border border-slate-200 bg-card p-4 text-[13px] text-slate-500">
                                Answers that need a closer look show up under <b>Needs your attention</b> on your home page. Everything else quietly files itself into care notes.
                            </div>
                        </TabsContent>

                        <TabsContent value="vitals" className="mt-4">
                            <VitalsPanel patientId={patientId} readings={readings} onChange={refresh} />
                        </TabsContent>

                        <TabsContent value="doctors" className="mt-4">
                            <TheirDoctors patientId={patientId} />
                        </TabsContent>
                    </Tabs>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200 text-[11px] text-slate-500 leading-relaxed">
                    <b>In an emergency, call 911.</b> Tanow is not for urgent medical help.
                </div>
            </div>

            <Dialog open={labDialog.open} onOpenChange={(v) => setLabDialog({ open: v, initial: v ? labDialog.initial : null })}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{labDialog.initial ? 'Edit lab result' : 'Add lab result'}</DialogTitle></DialogHeader>
                    <LabForm patientId={patientId} initial={labDialog.initial} onDone={() => { setLabDialog({ open: false, initial: null }); refresh(); }} />
                </DialogContent>
            </Dialog>

            <Dialog open={medDialog.open} onOpenChange={(v) => setMedDialog({ open: v, initial: v ? medDialog.initial : null })}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{medDialog.initial ? 'Edit medication' : 'Add medication'}</DialogTitle></DialogHeader>
                    <MedForm patientId={patientId} initial={medDialog.initial} onDone={() => { setMedDialog({ open: false, initial: null }); refresh(); }} />
                </DialogContent>
            </Dialog>

            <Dialog open={apptDialog.open} onOpenChange={(v) => setApptDialog({ open: v, initial: v ? apptDialog.initial : null })}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{apptDialog.initial ? 'Edit appointment' : 'Add appointment'}</DialogTitle></DialogHeader>
                    <ApptForm patientId={patientId} initial={apptDialog.initial} onDone={() => { setApptDialog({ open: false, initial: null }); refresh(); }} />
                </DialogContent>
            </Dialog>

            <Dialog open={logDialog.open} onOpenChange={(v) => setLogDialog({ open: v, initial: v ? logDialog.initial : null })}>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{logDialog.initial ? 'Edit care note' : 'Add care note'}</DialogTitle></DialogHeader>
                    <PatientLogForm
                        patientId={patientId}
                        initial={logDialog.initial}
                        onCreated={() => { setLogDialog({ open: false, initial: null }); refresh(); }}
                        onSaved={() => { setLogDialog({ open: false, initial: null }); refresh(); }}
                    />
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}
                title={`Delete ${confirmDel?.label || 'this'}?`}
                description="This can't be undone."
                onConfirm={doConfirmDelete}
            />

            <ConfirmDialog
                open={delMember} onOpenChange={setDelMember}
                title={`Remove ${maskName(patient.name)} from this household?`}
                description={`This removes ${maskName(patient.name)} and all of their records — appointments, medications, lab results, care notes, to-dos, and sent check-ins. This can't be undone.`}
                onConfirm={doDeleteMember}
                confirmLabel="Yes, remove them"
            />

            <Dialog open={editMember} onOpenChange={setEditMember}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit profile</DialogTitle></DialogHeader>
                    <EditMemberForm patient={patient} onSaved={() => { setEditMember(false); refresh(); }} />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function EditMemberForm({ patient, onSaved }) {
    const [f, setF] = useState({
        name: patient.name || '', dob: patient.dob || '', phone: patient.phone || '', email: patient.email || '',
        allergies: (patient.allergies || []).join(', '),
        conditions: (patient.conditions || []).join(', '),
        primary_provider: patient.primary_provider || '', primary_provider_id: patient.primary_provider_id || '',
    });
    const save = async () => {
        try {
            await api.patch(`/patients/${patient.patient_id}`, {
                name: f.name, dob: f.dob || null, phone: f.phone || null, email: f.email || null,
                allergies: f.allergies.split(',').map(s => s.trim()).filter(Boolean),
                conditions: f.conditions.split(',').map(s => s.trim()).filter(Boolean),
                primary_provider: f.primary_provider || null,
                primary_provider_id: f.primary_provider_id || '',
            });
            toast.success('Saved'); onSaved?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <>
            <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
                <div><Label>DOB</Label><Input type="date" value={f.dob} onChange={e => setF({ ...f, dob: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
                <div className="col-span-2"><Label>Email</Label><Input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></div>
                <div className="col-span-2"><Label>Allergies</Label><Input value={f.allergies} onChange={e => setF({ ...f, allergies: e.target.value })} /></div>
                <div className="col-span-2"><Label>Conditions</Label><Input value={f.conditions} onChange={e => setF({ ...f, conditions: e.target.value })} /></div>
                <div className="col-span-2">
                    <Label>Primary doctor</Label>
                    <DoctorPicker patientId={patient.patient_id} testid="member-primary-doctor-picker"
                        value={f.primary_provider ? { provider_id: f.primary_provider_id, name: f.primary_provider } : null}
                        onChange={(d) => setF({ ...f, primary_provider_id: d?.provider_id || '', primary_provider: d?.name || '' })} />
                </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full bg-primary text-primary-foreground">Save</Button></DialogFooter>
        </>
    );
}
